import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FlaskConical, Plus, RefreshCw, Trash2, AlertTriangle, CheckCircle, Beaker, TrendingDown, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Transformations() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';

  const [processes, setProcesses] = useState([]);
  const [runs, setRuns] = useState([]);
  const [dash, setDash] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [showNewRun, setShowNewRun] = useState(false);
  const [showNewProc, setShowNewProc] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, rRes, dRes, mRes] = await Promise.all([
        axios.get(`${API}/transformations/processes`, { withCredentials: true }),
        axios.get(`${API}/transformations/runs`, { params: { days: 60, anomaly_only: anomalyOnly }, withCredentials: true }),
        axios.get(`${API}/transformations/dashboard`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProcesses(pRes.data || []);
      setRuns(rRes.data?.items || []);
      setDash(dRes.data || null);
      setMaterials(mRes.data || []);
    } catch (err) {
      toast.error('Failed to load transformations');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anomalyOnly]);

  const acknowledge = async (run) => {
    const note = prompt('Acknowledgement note (optional)') || '';
    try {
      await axios.patch(`${API}/transformations/runs/${run.id}/acknowledge`, { note }, { withCredentials: true });
      toast.success('Anomaly acknowledged'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteRun = async (run) => {
    if (!window.confirm('Reverse & delete this run? Input lots will be restored and output lot removed.')) return;
    try {
      await axios.delete(`${API}/transformations/runs/${run.id}`, { withCredentials: true });
      toast.success('Run reversed'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete denied'); }
  };

  return (
    <div data-testid="transformations-page" className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FlaskConical className="text-amber-400" size={24} /> Raw-Material Transformations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Onion → onion oil, whole spices → powders etc. Each run deducts input, creates a new output lot, and flags anomalies if %loss is outside the tolerance band.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button onClick={() => setShowNewProc(true)} data-testid="tf-new-process-btn"
                className="px-3 py-2 text-xs rounded-lg border border-border bg-secondary hover:bg-accent text-foreground flex items-center gap-1.5">
                <Beaker size={13} /> New Process
              </button>
            </>
          )}
          <button onClick={() => setShowNewRun(true)} data-testid="tf-new-run-btn"
            className="px-3 py-2 text-xs rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 flex items-center gap-1.5">
            <Plus size={13} /> Log Run
          </button>
          <button onClick={load} className="p-2 rounded-lg border border-border bg-secondary hover:bg-accent text-foreground">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active Processes" value={dash?.active_processes ?? '—'} />
        <Kpi label="Runs Today" value={dash?.runs_today ?? '—'} />
        <Kpi label="Anomalies (30d)" value={dash?.anomalies_30d ?? '—'} color="text-amber-400" />
        <Kpi label="Processes" value={processes.length} />
      </div>

      {/* Avg yield by process */}
      {(dash?.avg_yield_by_process || []).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-xs text-muted-foreground uppercase mb-3">Avg Yield · Last 60 days</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {dash.avg_yield_by_process.map(p => (
              <div key={p._id || p.process_name} className="border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{p.process_name || '—'}</div>
                <div className="text-lg font-bold text-foreground mt-1">{p.avg_yield}%</div>
                <div className="text-[10px] text-muted-foreground">{p.runs} runs · {p.anomalies} anomalies</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Processes */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">Processes</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/60 text-muted-foreground text-[10px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Input</th>
                <th className="px-3 py-2 text-left">Output</th>
                <th className="px-3 py-2 text-left">Exp. Yield</th>
                <th className="px-3 py-2 text-left">Tolerance</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {processes.length === 0
                ? <tr><td colSpan={6} className="p-5 text-center text-muted-foreground">No transformation processes defined yet.</td></tr>
                : processes.map(p => (
                  <tr key={p.id} data-testid={`tf-proc-row-${p.id}`} className="border-t border-border">
                    <td className="px-3 py-1.5 text-foreground font-medium">{p.name}</td>
                    <td className="px-3 py-1.5 text-foreground">{p.input_material_name} <span className="text-muted-foreground">({p.input_unit})</span></td>
                    <td className="px-3 py-1.5 text-foreground">{p.output_material_name} <span className="text-muted-foreground">({p.output_unit})</span></td>
                    <td className="px-3 py-1.5 text-foreground">{p.expected_yield_pct}%</td>
                    <td className="px-3 py-1.5 text-foreground">±{p.tolerance_pct}%</td>
                    <td className="px-3 py-1.5">
                      {p.active
                        ? <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-green-900/40 text-green-300 border border-green-700">active</span>
                        : <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-zinc-800 text-muted-foreground border border-border">inactive</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Runs */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Transformation Runs</div>
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <input type="checkbox" checked={anomalyOnly} onChange={e => setAnomalyOnly(e.target.checked)} data-testid="tf-anomaly-only" />
            Show anomalies only
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/60 text-muted-foreground text-[10px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Date / Time</th>
                <th className="px-3 py-2 text-left">Process</th>
                <th className="px-3 py-2 text-left">Input → Output</th>
                <th className="px-3 py-2 text-left">Yield</th>
                <th className="px-3 py-2 text-left">%Loss</th>
                <th className="px-3 py-2 text-left">Operator</th>
                <th className="px-3 py-2 text-left">Output Lot</th>
                <th className="px-3 py-2 text-left">Flag</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="p-5 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                : runs.length === 0 ? <tr><td colSpan={9} className="p-5 text-center text-muted-foreground">No runs yet.</td></tr>
                  : runs.map(r => (
                    <tr key={r.id} data-testid={`tf-run-row-${r.id}`} className={`border-t border-border ${r.anomaly ? 'bg-amber-900/10' : ''}`}>
                      <td className="px-3 py-1.5 text-foreground font-mono">{r.run_date} <span className="text-muted-foreground">{r.run_time}</span></td>
                      <td className="px-3 py-1.5 text-foreground">{r.process_name}</td>
                      <td className="px-3 py-1.5 text-foreground">
                        {r.input_qty} {r.input_unit} {r.input_material_name} → <span className="text-emerald-400">{r.output_qty} {r.output_unit}</span>
                      </td>
                      <td className="px-3 py-1.5 text-foreground font-semibold">{r.yield_pct}%</td>
                      <td className="px-3 py-1.5"><span className="inline-flex items-center gap-1 text-amber-300"><TrendingDown size={11} /> {r.loss_pct}%</span></td>
                      <td className="px-3 py-1.5 text-foreground">{r.operator_name || '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-foreground">{r.output_lot_no || '—'}</td>
                      <td className="px-3 py-1.5">
                        {r.anomaly ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center gap-1 text-amber-300 text-[10px]" title={r.anomaly_reason}>
                              <AlertTriangle size={10} /> anomaly
                            </span>
                            {r.anomaly_acknowledged && <span className="text-[9px] text-green-400 flex items-center gap-1 mt-0.5"><CheckCircle size={8} /> ack</span>}
                          </div>
                        ) : <span className="inline-flex items-center gap-1 text-green-400 text-[10px]"><CheckCircle size={10} /> ok</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex gap-1">
                          {isAdmin && r.anomaly && !r.anomaly_acknowledged && (
                            <button onClick={() => acknowledge(r)} className="px-2 py-0.5 text-[10px] rounded bg-amber-600 hover:bg-amber-500 text-white" data-testid={`tf-ack-${r.id}`}>ack</button>
                          )}
                          {user?.role === 'admin' || user?.role === 'owner' ? (
                            <button onClick={() => deleteRun(r)} className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-900/30" data-testid={`tf-del-${r.id}`}>
                              <Trash2 size={11} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNewRun && <NewRunModal processes={processes} onClose={() => setShowNewRun(false)} onCreated={() => { setShowNewRun(false); load(); }} />}
      {showNewProc && <NewProcessModal materials={materials} onClose={() => setShowNewProc(false)} onCreated={() => { setShowNewProc(false); load(); }} />}
    </div>
  );
}

const Kpi = ({ label, value, color }) => (
  <div className="bg-card border border-border rounded-xl p-3">
    <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
    <div className={`text-xl font-bold mt-1 ${color || 'text-foreground'}`}>{value}</div>
  </div>
);

function NewRunModal({ processes, onClose, onCreated }) {
  const [form, setForm] = useState({ process_id: '', input_qty: '', output_qty: '', output_lot_no: '', expiry_date: '', remark: '' });
  const [saving, setSaving] = useState(false);
  const proc = processes.find(p => p.id === form.process_id);
  const yield_pct = form.input_qty > 0 ? ((parseFloat(form.output_qty || 0) / parseFloat(form.input_qty)) * 100).toFixed(2) : '—';

  const save = async () => {
    if (!form.process_id || !form.input_qty || form.output_qty === '') { toast.error('Process, input & output quantities are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/transformations/runs`, {
        process_id: form.process_id,
        input_qty: parseFloat(form.input_qty),
        output_qty: parseFloat(form.output_qty),
        output_lot_no: form.output_lot_no || undefined,
        expiry_date: form.expiry_date || undefined,
        remark: form.remark,
      }, { withCredentials: true });
      toast.success('Run logged'); onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="tf-new-run-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Log Transformation Run</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <Field label="Process *">
          <select value={form.process_id} data-testid="tf-run-process"
            onChange={e => setForm({ ...form, process_id: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground">
            <option value="">— Select —</option>
            {processes.filter(p => p.active !== false).map(p => <option key={p.id} value={p.id}>{p.name} · {p.input_material_name} → {p.output_material_name}</option>)}
          </select>
        </Field>
        {proc && (
          <div className="text-[10px] text-muted-foreground bg-secondary p-2 rounded border border-border">
            Expected {proc.expected_yield_pct}% yield · tolerance ±{proc.tolerance_pct}%
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Input ${proc?.input_unit ? '(' + proc.input_unit + ')' : ''}`}>
            <input type="number" value={form.input_qty} data-testid="tf-run-input-qty"
              onChange={e => setForm({ ...form, input_qty: e.target.value })}
              className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
          </Field>
          <Field label={`Output ${proc?.output_unit ? '(' + proc.output_unit + ')' : ''}`}>
            <input type="number" value={form.output_qty} data-testid="tf-run-output-qty"
              onChange={e => setForm({ ...form, output_qty: e.target.value })}
              className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
          </Field>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
          Yield this run: <span className="text-foreground font-bold">{yield_pct}%</span>
          {proc && yield_pct !== '—' && Math.abs(parseFloat(yield_pct) - proc.expected_yield_pct) > proc.tolerance_pct &&
            <span className="text-amber-400 inline-flex items-center gap-1"><AlertTriangle size={10} /> will flag anomaly</span>}
        </div>
        <Field label="Output lot # (auto if blank)">
          <input value={form.output_lot_no} onChange={e => setForm({ ...form, output_lot_no: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
        </Field>
        <Field label="Expiry date (optional)">
          <input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
        </Field>
        <Field label="Remark">
          <input value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs bg-secondary text-foreground">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="tf-run-save"
            className="px-3 py-1.5 rounded-lg text-xs bg-primary text-primary-foreground font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Log Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewProcessModal({ materials, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', input_material_id: '', output_material_id: '', expected_yield_pct: '', tolerance_pct: 10, notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name || !form.input_material_id || !form.output_material_id || !form.expected_yield_pct) {
      toast.error('Name, input, output and expected yield are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/transformations/processes`, {
        ...form,
        expected_yield_pct: parseFloat(form.expected_yield_pct),
        tolerance_pct: parseFloat(form.tolerance_pct),
      }, { withCredentials: true });
      toast.success('Process created'); onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="tf-new-proc-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">New Transformation Process</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <Field label="Name *">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="tf-proc-name"
            placeholder="Onion → Onion Oil" className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
        </Field>
        <Field label="Input raw material *">
          <select value={form.input_material_id} data-testid="tf-proc-input"
            onChange={e => setForm({ ...form, input_material_id: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground">
            <option value="">— Select —</option>
            {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
          </select>
        </Field>
        <Field label="Output raw material * (create this material first in Inventory if needed)">
          <select value={form.output_material_id} data-testid="tf-proc-output"
            onChange={e => setForm({ ...form, output_material_id: e.target.value })}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground">
            <option value="">— Select —</option>
            {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Expected yield %">
            <input type="number" value={form.expected_yield_pct} data-testid="tf-proc-yield"
              onChange={e => setForm({ ...form, expected_yield_pct: e.target.value })}
              placeholder="e.g. 12" className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
          </Field>
          <Field label="Tolerance ±%">
            <input type="number" value={form.tolerance_pct}
              onChange={e => setForm({ ...form, tolerance_pct: e.target.value })}
              className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
            className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs bg-secondary text-foreground">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="tf-proc-save"
            className="px-3 py-1.5 rounded-lg text-xs bg-primary text-primary-foreground font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] text-muted-foreground uppercase mb-0.5">{label}</div>
    {children}
  </label>
);
